import { Typography } from '@mui/material';
import {
  GridRenderCellParams,
  GridValueGetterParams,
} from '@mui/x-data-grid-pro';
import _ from 'lodash';
import { ExtendedGridColDef } from 'types/datagrid';
import { PurchaseRequestSchemaLite } from 'types/clientV2/purchaseRequests';
import { formatPrice } from 'utils/functions';
import { renderPartV2Cell } from 'ui-component/DataGrid/Render/RenderPartV2Cell';
import { V2OrgPartsFieldName } from 'ui-component/DataGrid/PartV2ColumnDef';
import { getApplyFilterFnV2OrgParts } from 'ui-component/DataGrid/FilterOperators';
import { renderWithLoadingWrapper } from 'ui-component/DataGrid/Render/RenderWithLoadingWrapper';
import { findV2OrgPartById } from 'utils/v2OrgParts';
import { FieldValues } from 'react-hook-form';
import { ALL_APPS } from 'constants/appConstants';
import { appPermissionAccessLevels } from 'types/apps';
import {
  UnitOfMeasureDetails,
  UnitOfMeasureOptions,
} from 'types/unitsOfMeasure';
import { useUpdatePurchaseRequestsV2Mutation } from 'store/slices/clientV2/purchaseRequests';
import * as yup from 'yup';

const editLineGetMutationArgs = (
  data: FieldValues,
  params: GridRenderCellParams
) => ({
  id: params.row.id,
  ...data,
});

export const makeBuyableColumns = ({
  tempUoM,
  editable = true,
  isLoadingOptimization = false,
  noRowHeightAuto = false,
}: {
  tempUoM: boolean;
  editable?: boolean;
  isLoadingOptimization?: boolean;
  noRowHeightAuto?: boolean;
}): ExtendedGridColDef[] => [
  {
    field: V2OrgPartsFieldName,
    headerName: 'Part',
    description: 'Part',
    type: 'string',
    minWidth: 250,
    flex: 1,
    valueGetter: (params: GridValueGetterParams) => {
      const part = findV2OrgPartById(
        params.row?.v2OrgParts,
        params.row?.v2OrgPart
      );
      return part?.mpn || params.row.description || '-';
    },
    renderCell: (params: GridRenderCellParams) => {
      const foundV2OrgPart = findV2OrgPartById(
        params.row?.v2OrgParts,
        params.row?.v2OrgPart
      );
      return foundV2OrgPart
        ? renderPartV2Cell({
            orgPart: foundV2OrgPart,
            altCount: (params.row?.altV2OrgParts ?? []).length,
            unapprovedAltCount: (params.row?.unapprovedAltV2OrgParts ?? [])
              .length,
            noRowHeightAuto,
          })
        : params.row.description || '-';
    },
    getApplyQuickFilterFn: getApplyFilterFnV2OrgParts,
  },
  {
    field: 'quant',
    headerName: 'Quantity',
    type: 'number',
    minWidth: 120,
    flex: 0.75,
    editable,
    inlineEditRenderCell: true,
    useMutation: useUpdatePurchaseRequestsV2Mutation,
    mutationAsClientV2: true,
    getMutationArgs: editLineGetMutationArgs,
    mutationValidation: yup
      .number()
      .required('Quantity is required')
      .moreThan(0, 'Quantity must be greater than 0')
      .typeError('Quantity must be a number'),
    editPermissionScope: {
      app: ALL_APPS.SIMPLIFIED_BUYING_ORDERS.id,
      accessLevel: appPermissionAccessLevels.edit,
    },
    valueGetter: (params: GridValueGetterParams) => {
      const row = params.row as PurchaseRequestSchemaLite & {
        optimizedQuant?: number;
      };
      return tempUoM && params.row.dimensionValue
        ? _.toNumber(params.row.dimensionValue)
        : _.toNumber(row.quant) || 0;
    },
    renderCell: (params: GridRenderCellParams<number>) => {
      const value = params.value
        ? _.toNumber(params.value).toLocaleString('en-US')
        : '0';

      const unitOfMeasure = tempUoM
        ? UnitOfMeasureDetails[
            params.row.dimensionUnit as UnitOfMeasureOptions
          ] || UnitOfMeasureDetails[UnitOfMeasureOptions.PIECE]
        : undefined;

      const valueString = unitOfMeasure?.abbreviation
        ? `${value} ${unitOfMeasure.abbreviation}`
        : value;

      return <Typography align="right">{valueString}</Typography>;
    },
    mutationPreSubmit: (data: FieldValues) =>
      tempUoM
        ? {
            dimensionValue: data.quant,
          }
        : {
            quant: data.quant,
          },
  },
  {
    field: 'unitPrice',
    headerName: 'Est. Unit Price',
    description: 'Per-unit cost',
    type: 'number',
    minWidth: 150,
    flex: 0.75,
    valueGetter: (params: GridValueGetterParams) => {
      const row = params.row as PurchaseRequestSchemaLite & {
        optimizedUnitPrice?: number;
        optimizedQuant?: number;
      };
      if (row.optimizedUnitPrice !== undefined) {
        return row.optimizedUnitPrice;
      }
      const quant = _.toNumber(row.quant) || 0;
      const lineTotal = _.toNumber(row.targetLineTotal) || 0;
      return quant > 0 ? lineTotal / quant : 0;
    },
    renderCell: (params: GridRenderCellParams<number>) =>
      renderWithLoadingWrapper({
        isLoading: isLoadingOptimization,
        params,
        renderMethod: () => (
          <Typography align="right">
            {params.value ? formatPrice(params.value) : '-'}
          </Typography>
        ),
        loadingVariant: 'skeleton',
      }),
  },
  {
    field: 'lineTotal',
    headerName: 'Est. Line Total',
    description: 'Quantity × unit price',
    type: 'number',
    minWidth: 150,
    flex: 0.75,
    valueGetter: (params: GridValueGetterParams) => {
      const row = params.row as PurchaseRequestSchemaLite & {
        optimizedLineTotal?: number;
      };
      return row.optimizedLineTotal ?? (_.toNumber(row.targetLineTotal) || 0);
    },
    renderCell: (params: GridRenderCellParams<number>) =>
      renderWithLoadingWrapper({
        isLoading: isLoadingOptimization,
        params,
        renderMethod: () => (
          <Typography align="right">
            {params.value ? formatPrice(params.value) : '-'}
          </Typography>
        ),
        loadingVariant: 'skeleton',
      }),
  },
];
